import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';

export function CartEmailDialog({
  email,
  setEmail,
  sending,
  error,
  sent,
  returnFocus,
  onSend,
  onClose,
}: {
  email: string;
  setEmail(value: string): void;
  sending: boolean;
  error: string | null;
  sent: boolean;
  returnFocus: HTMLElement | null;
  onSend(): void;
  onClose(): void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
    return () => {
      returnFocus?.focus();
    };
  }, [returnFocus]);
  useEffect(() => {
    if (sending) dialog.current?.focus();
    else if (document.activeElement === dialog.current) input.current?.focus();
  }, [sending]);

  const handleKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !sending) {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled])',
      ),
    );
    const first = controls[0];
    const last = controls.at(-1);
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    if (event.shiftKey && document.activeElement === first && last) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last && first) {
      event.preventDefault();
      first.focus();
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSend();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-email-title"
        data-name="cart-email-dialog"
        tabIndex={-1}
        onKeyDown={handleKeys}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <h2 id="cart-email-title" className="text-lg font-semibold text-gray-800">
          Email cart
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          We&apos;ll email your complete Cart summary and drawings as a PDF.
        </p>
        <form onSubmit={submit} noValidate className="mt-5">
          <label htmlFor="cart-email-address" className="block text-sm font-medium text-gray-800">
            Email address
          </label>
          <input
            ref={input}
            id="cart-email-address"
            data-name="cart-email-address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={sending}
            aria-invalid={!!error}
            className="mt-2 min-h-11 w-full rounded-xl border border-gray-300 px-3 text-sm focus-visible:outline-2 focus-visible:outline-progress"
          />
          {error && (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {error}
            </p>
          )}
          {sent && (
            <p role="status" className="mt-2 text-sm text-gray-700">
              Email sent. Delivery to your inbox is not guaranteed.
            </p>
          )}
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              data-name="cart-email-cancel"
              disabled={sending}
              onClick={onClose}
              className="min-h-11 rounded-xl border border-gray-200 px-4 text-sm text-gray-700 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-name="cart-email-send"
              disabled={sending}
              className="min-h-11 rounded-xl bg-progress px-5 text-sm font-semibold text-white disabled:opacity-40"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
